/**
 * LoginForm Component
 *
 * Renders the login form for user authentication.
 * Includes password visibility toggle, forgotten password flow,
 * and a modal for account activation/password reset requests.
 *
 * @param {Object} props - Component props
 * @param {Object} props.credentials - Object with email and password
 * @param {Function} props.setCredentials - State setter for credentials
 * @param {Function} props.handleLogin - Callback to submit login
 * @param {string} props.response - Alert message to display
 * @param {Function} props.handleResendConfirmation - Callback to resend confirmation email
 * @param {Function} props.checkPwdLength - Password validation functions
 * @param {Function} props.checkPwdSymbol - etc...
 * @param {Function} props.measurePwdLength\n * @returns {JSX.Element} Login form with modal for additional options\n */
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import "./login-form.css";
import { Card, Button, Form, Modal } from "react-bootstrap";
import { BsDot } from "react-icons/bs";
import HeaderLogo from "../../../assets/navbar/roiquant-600x200px-white-01.png";
import AlertBannerLogin from "../../layout/navbar/alert-banner-login";
import { Turnstile } from "@marsidev/react-turnstile";
function LoginForm(props) {
  // Track password field visibility
  const [passwordShown, setPasswordShown] = useState(false);

  // Toggle show/hide password
  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  // Track modal (unable to login form) visibility
  const [openModal, setOpenModal] = useState(false);

  // Open the "Can't log in" modal
  function openForm() {
    setOpenModal(true);
  }

  // Close the "Can't log in" modal
  function hideForm() {
    setOpenModal(false);
  }

  // Track selected option in the modal (verification email or password reset)
  const [option, setOption] = useState(
    "I'd like to request a verification email to activate my new account",
  );

  // Handle change of selected option in modal
  function handleChange(e) {
    e.preventDefault();
    let value = e.target.value;
    setOption(value);
  }

  // History object for navigation
  const history = useHistory();

  // Navigate to password reset page, pre-filling email if available
  function directToResetPasswordPage() {
    history.push(
      "/reset-password/?email=" + encodeURIComponent(props.credentials.email),
    );
  }

  // Track CAPTCHA completion for login button
  const [captchaCompleted, setCaptchaCompleted] = useState(false);
  // Track CAPTCHA completion for modal (resend email or password reset)
  const [captchaTwoCompleted, setCaptchaTwoCompleted] = useState(false);

  return (
    <>
      {/* <AlertBannerLogin /> */}

      <div id="logincontent">
        <div className="logintop">
          <a
            href="https://www.roiquant.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={HeaderLogo}
              alt="roiquant"
              width="180px"
              height="60px"
              className="roiquantlogo"
            />
          </a>
        </div>

        <div className="createaccalerttop">{props.response}</div>

        <div id="loginform">
          <Card className="logincard">
            <div className="loginheader">
              <h3>Log into your roiquant account</h3>
            </div>

            <div className="createaccnote">
              <p>
                Don't have an account?{" "}
                <a href="/create-account">Create free account</a>
              </p>
            </div>

            <Form>
              <Form.Group className="inputgroup" controlId="formBasicEmail">
                <Form.Control
                  className="inputbox"
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={props.credentials.email}
                  onChange={(e) =>
                    props.setCredentials(
                      Object.assign({}, props.credentials, {
                        email: e.target.value,
                      }),
                    )
                  }
                />
              </Form.Group>
              {/* Real-time password strength validation feedback */}
              <Form.Group className="inputgroup" controlId="formBasicPassword">
                <Form.Control
                  className="inputbox"
                  type={passwordShown ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  onChange={(e) =>
                    props.setCredentials(
                      Object.assign({}, props.credentials, {
                        password: e.target.value,
                      }),
                    )
                  }
                />

                {/* Toggle to show/hide password */}
                <a href="#" className="showpassword" onClick={togglePassword}>
                  {passwordShown ? "Hide" : "Show"} password
                </a>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    marginTop: "15px",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    id="checksField"
                    style={{ width: "50%", paddingLeft: "15%" }}
                  >
                    <p
                      id="char"
                      style={{
                        color:
                          props.measurePwdLength(props.credentials.password) ==
                          0
                            ? "grey"
                            : props.checkPwdLength(props.credentials.password)
                              ? "green"
                              : "red",
                      }}
                    >
                      At least 8 characters or as long as possible
                    </p>

                    <p
                      id="char"
                      style={{
                        color:
                          props.measurePwdLength(props.credentials.password) ==
                          0
                            ? "grey"
                            : props.checkPwdSymbol(props.credentials.password)
                              ? "green"
                              : "red",
                      }}
                    >
                      Include at least 1 symbol character
                    </p>

                    <p
                      id="char"
                      style={{
                        color:
                          props.measurePwdLength(props.credentials.password) ==
                          0
                            ? "grey"
                            : props.checkPwdNum(props.credentials.password)
                              ? "green"
                              : "red",
                      }}
                    >
                      Include at least 1 number
                    </p>
                  </div>

                  <div id="checksField" style={{ width: "50%" }}>
                    <p
                      id="char"
                      style={{
                        color:
                          props.measurePwdLength(props.credentials.password) ==
                          0
                            ? "grey"
                            : props.checkPwdLowercase(
                                  props.credentials.password,
                                )
                              ? "green"
                              : "red",
                      }}
                    >
                      At least 1 lowercase letter
                    </p>

                    <p
                      id="char"
                      style={{
                        color:
                          props.measurePwdLength(props.credentials.password) ==
                          0
                            ? "grey"
                            : props.checkPwdUppercase(
                                  props.credentials.password,
                                )
                              ? "green"
                              : "red",
                      }}
                    >
                      At least 1 uppercase letter
                    </p>
                  </div>
                </div>
              </Form.Group>

              {/* CAPTCHA verification before login */}
              <Turnstile
                siteKey={process.env.REACT_APP_TURNSTILE}
                onSuccess={(token) => {
                  setCaptchaCompleted(true);
                  props.setCaptchaToken(token);
                }}
                onExpire={() => {
                  setCaptchaCompleted(false);
                  props.setCaptchaToken("");
                }}
                onError={() => {
                  setCaptchaCompleted(false);
                  props.setCaptchaToken("");
                }}
                options={{
                  size: "flexible", // or 'normal', or 'compact'
                  theme: "light",
                }}
              />

              {/* Submit button - only enabled after CAPTCHA completion */}
              <div style={{ textAlign: "center", marginBottom: "-15px" }}>
                {captchaCompleted ? (
                  <Button
                    className="loginbtn glintanimation"
                    type="submit"
                    onClick={(event) => {
                      event.preventDefault();
                      props.handleLogin({
                        ...props.credentials,
                        captchaToken: props.captchaToken,
                      });
                    }}
                  >
                    <span>Log in</span>
                    {/* Links for password reset and support modal trigger */}
                  </Button>
                ) : (
                  <Button
                    className="loginbtn glintanimation"
                    type="submit"
                    disabled
                  >
                    <span>Log in</span>
                  </Button>
                )}
              </div>

              <div className="forgotpw">
                <a href="/reset-password">Forgot password?</a>
                <BsDot />
                <a onClick={openForm}>Still can't log in?</a>
              </div>
            </Form>

            {/* Modal for users who can't log in (resend verification email or reset password) */}
          </Card>
        </div>
      </div>

      <Modal
        className="getintouchformdiv"
        show={openModal}
        onHide={hideForm}
        backdrop="static"
        fullscreen="sm-down"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton
          className="getintouchformheader"
          style={{ borderBottom: "none" }}
        />
        <Modal.Body className="getintouchformbody">
          <div className="loginheader">
            <h3>Still can't log in?</h3>
          </div>

          <div>
            <Form>
              Email<sup>*</sup>
              <Form.Group className="inputgroup" controlId="formBasicEmail">
                <Form.Control
                  className="inputbox"
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={props.credentials.email}
                  onChange={(e) =>
                    props.setCredentials(
                      Object.assign({}, props.credentials, {
                        email: e.target.value,
                      }),
                    )
                  }
                />
              </Form.Group>
              Request<sup>*</sup>
              <Form.Group className="inputgroup" controlId="formBasicAbout">
                <Form.Select
                  required
                  className="formbox"
                  name="request"
                  value={option}
                  placeholder=""
                  onChange={handleChange}
                >
                  <option>
                    I'd like to request a verification email to activate my new
                    account
                  </option>
                  {/* Show CAPTCHA only for verification email option */}
                  <option>
                    I'd like to set a new password to log into my account
                  </option>
                </Form.Select>
              </Form.Group>
              {/* TODO: (Optional) If possible, turn on Turnstile if user wants to request a verification email */}
              {option ===
              "I'd like to request a verification email to activate my new account" ? (
                <Turnstile
                  siteKey={process.env.REACT_APP_TURNSTILE}
                  onSuccess={(token) => {
                    setCaptchaTwoCompleted(true);
                    props.setCaptchaTwoToken(token);
                  }}
                  onExpire={() => {
                    setCaptchaTwoCompleted(false);
                    props.setCaptchaTwoToken("");
                  }}
                  onError={() => {
                    setCaptchaTwoCompleted(false);
                    props.setCaptchaTwoToken("");
                  }}
                  options={{
                    size: "flexible", // or 'normal', or 'compact'
                    theme: "light",
                  }}
                />
              ) : (
                ""
              )}
              {/* Action button - changes based on selected option */}
              <div style={{ textAlign: "center" }}>
                {/* TODO: Disable resend button when captchaTwoCompleted is false */}
                {option ===
                  "I'd like to request a verification email to activate my new account" &&
                !captchaTwoCompleted ? ( // verify & false, disable button
                  <Button className="loginbtn" disabled>
                    <span>Re-send verification email</span>
                  </Button>
                ) : option ===
                    "I'd like to request a verification email to activate my new account" &&
                  captchaTwoCompleted ? ( // verify & true, enable button
                  <Button
                    className="loginbtn glintanimation"
                    type="submit"
                    onClick={(event) => {
                      event.preventDefault();
                      props.handleResendConfirmation(
                        props.credentials.email,
                        props.captchaTwoToken,
                      );
                    }}
                  >
                    <span>Re-send verification email</span>
                  </Button>
                ) : option ===
                  "I'd like to set a new password to log into my account" ? ( // reset, enable button
                  <Button
                    className="loginbtn glintanimation"
                    onClick={directToResetPasswordPage}
                  >
                    <span>Submit</span>
                  </Button>
                ) : (
                  ""
                )}
              </div>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default LoginForm;
